import { useMutation } from '@apollo/client/react';
import { Button, ButtonProps, Text, VStack } from '@chakra-ui/react';
import { ADMIN_CREATE_LOGIN_AS_USER_TOKEN } from '@src/gql/mutations/AdminCreateLoginAsUserToken';
import { scoBackendUrl } from '@src/lib/startups-url';
import React, { useState } from 'react';
type LoginAsUserButtonProps = Omit<ButtonProps, 'onClick'> & {
  /** The user to log in as */
  userId: string;
};

/**
 * Mints a one-time login-as token from this admin session and redeems it in this tab. The session
 * switches to the user, so the admin is signed out.
 */
export const LoginAsUserButton = ({
  userId,
  children,
  ...buttonProps
}: LoginAsUserButtonProps) => {
  const [createToken] = useMutation(ADMIN_CREATE_LOGIN_AS_USER_TOKEN);
  const [redirecting, setRedirecting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const handleClick = async () => {
    setError(null);
    setRedirecting(true);
    try {
      const {
        data
      } = await createToken({
        variables: {
          input: {
            userId
          }
        }
      });
      const token = data?.adminCreateLoginAsUserToken?.token;
      if (!token) {
        throw new Error('No login-as token returned');
      }
      window.location.assign(scoBackendUrl('/auth/as-user', {
        token
      }));
    } catch (err) {
      setRedirecting(false);
      setError(err instanceof Error && err.message ? err.message : 'Could not log in as this user');
    }
  };
  return <VStack align={'stretch'} gap={1}>
      <Button loading={redirecting} size={'sm'} onClick={handleClick} {...buttonProps}>
        {children || 'Login as User'}
      </Button>
      {error && <Text color={'fg.error'} fontSize={'xs'}>
          {error}
        </Text>}
    </VStack>;
};